import { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, ResponsiveContainer,
} from 'recharts';
import { getCategories, getStoreComparison } from '../services/api';

const CHAIN_COLORS = {
  'Kiwi': '#22c55e',
  'Rema 1000': '#ef4444',
  'Coop': '#3b82f6',
  'Meny': '#a855f7',
  'Spar': '#f59e0b',
};

function colorForChain(chain) {
  return CHAIN_COLORS[chain] || '#94a3b8';
}

export default function StoreComparisonPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getCategories().then((cats) => {
      setCategories(cats);
      if (cats.length > 0) setSelectedCat(String(cats[0].id));
    });
  }, []);

  useEffect(() => {
    if (!selectedCat) return;
    setLoading(true);
    getStoreComparison(selectedCat)
      .then(setData)
      .finally(() => setLoading(false));
  }, [selectedCat]);

  const selectedCategory = categories.find((c) => String(c.id) === selectedCat);
  const cheapest = data.length > 0 ? data[0].store_chain : null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 style={{ marginBottom: '0.25rem' }}>Store comparison</h2>
          <p className="muted" style={{ margin: 0 }}>Average price per canonical unit — last 90 days</p>
        </div>
        <select
          value={selectedCat}
          onChange={(e) => setSelectedCat(e.target.value)}
          style={{ width: 220, maxWidth: '100%' }}
        >
          {categories.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>

      {loading && <p className="muted">Loading…</p>}

      {!loading && data.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-muted)' }}>
          No data for this category in the last 90 days.
        </div>
      )}

      {!loading && data.length > 0 && (
        <>
          {cheapest && (
            <div className="card" style={{ marginBottom: '1rem', background: 'var(--color-success-bg)', borderColor: 'var(--color-success-border)' }}>
              <strong style={{ color: 'var(--color-success-text-strong)' }}>{cheapest}</strong>
              <span style={{ color: 'var(--color-success-text)', fontSize: 13 }}> is cheapest for {selectedCategory?.name} over the last 90 days</span>
            </div>
          )}
          <div className="card">
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>{selectedCategory?.name}</strong>
              {selectedCategory && <span className="muted"> per {selectedCategory.canonical_unit}</span>}
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-chart-grid)" vertical={false} />
                <XAxis dataKey="store_chain" tick={{ fontSize: 13, fill: 'var(--color-chart-axis)' }} />
                <YAxis tick={{ fontSize: 12, fill: 'var(--color-chart-axis)' }} unit=" kr" />
                <Tooltip
                  formatter={(value) => [`${Number(value).toFixed(2)} kr/${selectedCategory?.canonical_unit}`, 'Avg price']}
                  contentStyle={{
                    fontSize: 13,
                    borderRadius: 6,
                    background: 'var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    color: 'var(--color-text)',
                  }}
                  labelStyle={{ color: 'var(--color-text-muted)' }}
                  itemStyle={{ color: 'var(--color-text)' }}
                  cursor={{ fill: 'var(--color-border)', opacity: 0.3 }}
                />
                <Bar dataKey="avg_price" radius={[4, 4, 0, 0]}>
                  {data.map((entry) => (
                    <Cell key={entry.store_chain} fill={colorForChain(entry.store_chain)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            <div className="table-scroll" style={{ marginTop: '1.5rem' }}>
            <table>
              <thead>
                <tr>
                  <th>Chain</th>
                  <th style={{ textAlign: 'right' }}>Avg price</th>
                  <th style={{ textAlign: 'right' }}>Observations</th>
                  <th style={{ textAlign: 'right' }}>vs cheapest</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => {
                  const cheapestPrice = Number(data[0].avg_price);
                  const premium = i === 0 ? 0 : ((Number(row.avg_price) - cheapestPrice) / cheapestPrice * 100);
                  return (
                    <tr key={row.store_chain}>
                      <td style={{ fontWeight: i === 0 ? 600 : 400 }}>{row.store_chain}</td>
                      <td style={{ textAlign: 'right' }}>
                        {Number(row.avg_price).toFixed(2)} kr/{row.canonical_unit}
                      </td>
                      <td style={{ textAlign: 'right', color: 'var(--color-text-muted)' }}>{row.observation_count}</td>
                      <td style={{ textAlign: 'right', color: i === 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
                        {i === 0 ? 'cheapest' : `+${premium.toFixed(1)}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
